import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../auth/AuthProvider";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";

const API_URL = process.env.REACT_APP_API_URL;

const UNASSIGNED = "Unassigned";
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

// Badge shown next to classified categories
const SourceBadge = ({ source }) => {
  if (source === "ai") {
    return (
      <span
        className="ml-1 text-xs text-purple-400 font-medium select-none"
        title="AI classified"
      >
        ✦
      </span>
    );
  }
  if (source === "manual") {
    return (
      <span
        className="ml-1 text-xs text-blue-400 select-none"
        title="User Defined"
      >
        ✎
      </span>
    );
  }
  return null;
};

const CategorizeTransactions = () => {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const authHeaders = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const [transactions, setTransactions]       = useState([]);
  const [accounts, setAccounts]               = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("all");
  // { transaction_id: { category_name, merchant_name, source } }
  const [userAssignments, setUserAssignments] = useState({});
  const [categories, setCategories]           = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [classifying, setClassifying]         = useState(false);
  const [notLinked, setNotLinked]             = useState(false);
  const [saving, setSaving]                   = useState({});
  const [notes, setNotes]                     = useState({});

  // Category manager
  const [newCatName, setNewCatName]           = useState("");
  const [savingCat, setSavingCat]             = useState(false);
  const [showCatManager, setShowCatManager]   = useState(false);
  const [showCatFilter, setShowCatFilter]     = useState(false);

  // Bulk keyword assign
  const [showBulkAssign, setShowBulkAssign]   = useState(false);
  const [bulkKeyword, setBulkKeyword]         = useState("");
  const [bulkCategory, setBulkCategory]       = useState("");
  const [bulkApplying, setBulkApplying]       = useState(false);

  // Filters / sort / pagination
  const [filterCategory, setFilterCategory]   = useState("all");
  const [search, setSearch]                   = useState("");
  const [sortField, setSortField]             = useState("date");
  const [sortDir, setSortDir]                 = useState("desc");
  const [pageSize, setPageSize]               = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage]         = useState(1);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const pageNumbers = (total, current) => {
    const pages = [];
    const delta = 2;
    const left  = Math.max(1, current - delta);
    const right = Math.min(total, current + delta);
    if (left > 1)     { pages.push(1);     if (left > 2)          pages.push("…"); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total) { if (right < total - 1) pages.push("…"); pages.push(total); }
    return pages;
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  // ── AI classification ──────────────────────────────────────────────────────
  const runClassify = useCallback(async (txns, existingAssignments) => {
    // Only send transactions that have no assignment yet
    const unclassified = txns.filter((t) => !existingAssignments[t.transaction_id]);
    if (!unclassified.length) return;

    setClassifying(true);
    try {
      const res = await fetch(`${API_URL}/api/bank/classify`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          transactions: unclassified.map((t) => ({
            transaction_id:  t.transaction_id,
            merchant_name:   t.merchant_name || "",
            name:            t.name || "",
            amount:          t.amount,
            bank_category: t.bank_category || "",
            account_type:    t.account_type || "",
          })),
        }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const classified = data.classified || {};
      if (!Object.keys(classified).length) return;

      setUserAssignments((prev) => {
        const updates = {};
        Object.entries(classified).forEach(([txn_id, info]) => {
          // Only apply if still unassigned (user may have manually set one during the request)
          if (!prev[txn_id]) {
            updates[txn_id] = {
              category_name: info.category_name,
              merchant_name: "",
              source: info.source,
            };
          }
        });
        return { ...prev, ...updates };
      });
    } catch { /* non-critical — page still works without classification */ }
    finally {
      setClassifying(false);
    }
  }, [authHeaders]);

  // ── Initial data load ──────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const statusRes = await fetch(`${API_URL}/api/bank/status`, { headers: authHeaders });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        if (!statusData.linked) { setNotLinked(true); setLoading(false); return; }
      }

      const [txRes, assignRes, catRes, notesRes] = await Promise.all([
        fetch(`${API_URL}/api/bank/transactions?days=90`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bank/transactions/categories`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bank/categories`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bank/transactions/notes`, { headers: authHeaders }),
      ]);

      let loadedTxns = [], loadedAssignments = {};

      if (txRes.ok) {
        const txData = await txRes.json();
        loadedTxns = txData.transactions || [];
        setTransactions(loadedTxns);

        // Derive unique accounts from transactions
        const seen = new Map();
        loadedTxns.forEach((t) => {
          if (t.account_id && !seen.has(t.account_id)) {
            seen.set(t.account_id, t.account_owner || t.account_id);
          }
        });
        // Enrich with real account names from balance endpoint
        const balRes = await fetch(`${API_URL}/api/bank/balance`, { headers: authHeaders });
        if (balRes.ok) {
          const balData = await balRes.json();
          (balData.accounts || []).forEach((a) => seen.set(a.account_id, a.name));
        }
        setAccounts(Array.from(seen.entries()).map(([account_id, name]) => ({ account_id, name })));
      }

      if (assignRes.ok) {
        loadedAssignments = await assignRes.json();
        setUserAssignments(loadedAssignments);
      }
      if (catRes.ok)    setCategories(await catRes.json());
      if (notesRes.ok)  setNotes(await notesRes.json());

      // Set classifying here so it batches with setLoading(false) in the same render
      const hasUnclassified = loadedTxns.some((t) => !loadedAssignments[t.transaction_id]);
      if (hasUnclassified) setClassifying(true);

      // Kick off AI classification for unclassified transactions (non-blocking)
      runClassify(loadedTxns, loadedAssignments);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders, runClassify]);

  useEffect(() => {
    if (auth.isAuthenticated) fetchAll();
  }, [auth.isAuthenticated, fetchAll]);

  // ── Effective category for display ────────────────────────────────────────
  const effectiveCategory = (txn) => {
    const assignment = userAssignments[txn.transaction_id];
    if (assignment) return assignment.category_name;
    return UNASSIGNED;
  };

  // ── Category change (manual override) ────────────────────────────────────
  const handleCategoryChange = async (txn, newCat) => {
    const prevAssignment = userAssignments[txn.transaction_id];
    const prevCat = prevAssignment?.category_name || UNASSIGNED;
    if (newCat === prevCat) return;

    const normalizedMerchant = (txn.merchant_name || txn.name || "").trim().toLowerCase();
    const displayName        = txn.merchant_name || txn.name || normalizedMerchant;

    setSaving((s) => ({ ...s, [txn.transaction_id]: true }));

    // Optimistic update for this transaction only
    setUserAssignments((prev) => ({
      ...prev,
      [txn.transaction_id]: { category_name: newCat, merchant_name: normalizedMerchant, source: "manual" },
    }));

    try {
      const res = await fetch(`${API_URL}/api/bank/transactions/categorize`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          transaction_id:    txn.transaction_id,
          merchant_name:     txn.merchant_name || txn.name || "",
          category_name:     newCat,
          previous_category: prevCat === UNASSIGNED ? "" : prevCat,
        }),
      });
      if (!res.ok) throw new Error("Failed to save category");

      // Find other same-merchant transactions that haven't been manually set
      const siblings = transactions.filter(
        (t) =>
          t.transaction_id !== txn.transaction_id &&
          (t.merchant_name || t.name || "").trim().toLowerCase() === normalizedMerchant &&
          (!userAssignments[t.transaction_id] || userAssignments[t.transaction_id].source !== "manual")
      );

      if (siblings.length > 0) {
        const result = await Swal.fire({
          title: `Update all "${displayName}" transactions?`,
          text: `${siblings.length} other transaction${siblings.length !== 1 ? "s" : ""} from "${displayName}" will also be set to "${newCat}".`,
          icon: "question",
          showDenyButton: true,
          showCancelButton: false,
          confirmButtonText: `Update all ${siblings.length + 1}`,
          denyButtonText: "Just this one",
          confirmButtonColor: "#22c55e",
          denyButtonColor: "#6b7280",
        });

        if (result.isConfirmed) {
          setUserAssignments((prev) => {
            const updates = {};
            siblings.forEach((t) => {
              updates[t.transaction_id] = { category_name: newCat, merchant_name: normalizedMerchant, source: "manual" };
            });
            return { ...prev, ...updates };
          });
          fetch(`${API_URL}/api/bank/transactions/bulk-categorize`, {
            method: "POST",
            headers: authHeaders,
            body: JSON.stringify({
              items: siblings.map((t) => ({
                transaction_id: t.transaction_id,
                merchant_name:  t.merchant_name || t.name || "",
                category_name:  newCat,
              })),
            }),
          }).catch(() => {});
        }
      }
    } catch (err) {
      // Roll back optimistic update
      setUserAssignments((prev) => {
        const next = { ...prev };
        if (prevAssignment) {
          next[txn.transaction_id] = prevAssignment;
        } else {
          delete next[txn.transaction_id];
        }
        return next;
      });
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[txn.transaction_id]; return n; });
    }
  };

  // ── Notes ─────────────────────────────────────────────────────────────────
  const handleNoteBlur = async (txn, value) => {
    const trimmed = value.trim();
    setNotes((prev) => {
      const next = { ...prev };
      if (trimmed) next[txn.transaction_id] = trimmed;
      else delete next[txn.transaction_id];
      return next;
    });
    try {
      await fetch(`${API_URL}/api/bank/transactions/note`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ transaction_id: txn.transaction_id, note: trimmed }),
      });
    } catch { /* non-critical */ }
  };

  // ── Category manager ──────────────────────────────────────────────────────
  const handleAddCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setSavingCat(true);
    try {
      const res = await fetch(`${API_URL}/api/bank/categories`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to create category");
      }
      const created = await res.json();
      setCategories((prev) => [...prev, created]);
      setNewCatName("");
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setSavingCat(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    const result = await Swal.fire({
      icon: "warning",
      title: `Delete "${cat.name}"?`,
      text: "Transactions assigned to this category will show as Unassigned.",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_URL}/api/bank/categories/${cat.category_id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error("Failed to delete category");
      setCategories((prev) => prev.filter((c) => c.category_id !== cat.category_id));
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    }
  };

  // ── Bulk keyword assign ───────────────────────────────────────────────────
  const bulkMatchCount = useMemo(() => {
    const kw = bulkKeyword.trim().toLowerCase();
    if (!kw) return 0;
    return transactions.filter((t) => {
      const name = (t.merchant_name || t.name || "").toLowerCase();
      return name.includes(kw) && userAssignments[t.transaction_id]?.source !== "manual";
    }).length;
  }, [transactions, bulkKeyword, userAssignments]);

  const handleBulkAssign = async () => {
    const kw = bulkKeyword.trim().toLowerCase();
    if (!kw || !bulkCategory) return;
    const matches = transactions.filter((t) => {
      const name = (t.merchant_name || t.name || "").toLowerCase();
      return name.includes(kw) && userAssignments[t.transaction_id]?.source !== "manual";
    });
    if (!matches.length) return;

    setBulkApplying(true);
    setUserAssignments((prev) => {
      const updates = {};
      matches.forEach((t) => {
        updates[t.transaction_id] = {
          category_name: bulkCategory,
          merchant_name: (t.merchant_name || t.name || "").trim().toLowerCase(),
          source: "manual",
        };
      });
      return { ...prev, ...updates };
    });
    await fetch(`${API_URL}/api/bank/transactions/bulk-categorize`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({
        items: matches.map((t) => ({
          transaction_id: t.transaction_id,
          merchant_name:  t.merchant_name || t.name || "",
          category_name:  bulkCategory,
        })),
      }),
    });
    setBulkApplying(false);
    setBulkKeyword("");
    Swal.fire({
      icon: "success",
      title: "Applied!",
      text: `${matches.length} transaction${matches.length !== 1 ? "s" : ""} assigned to "${bulkCategory}".`,
      timer: 2000,
      showConfirmButton: false,
    });
  };

  // ── Derived display data ──────────────────────────────────────────────────
  const accountMap = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.account_id, a.name])),
    [accounts]
  );

  const categoryOptions = [
    UNASSIGNED,
    ...[...categories.map((c) => c.name)].sort((a, b) => a.localeCompare(b)),
  ];

  const accountFilteredTxns = useMemo(() => (
    selectedAccount === "all"
      ? transactions
      : transactions.filter((t) => t.account_id === selectedAccount)
  ), [transactions, selectedAccount]);

  const filteredTransactions = useMemo(() => {
    return accountFilteredTxns.filter((txn) => {
      const cat = effectiveCategory(txn);
      const matchesCat = filterCategory === "all" || cat === filterCategory;
      const q = search.toLowerCase();
      const matchesSearch =
        !q ||
        (txn.name || "").toLowerCase().includes(q) ||
        (txn.merchant_name || "").toLowerCase().includes(q);
      return matchesCat && matchesSearch;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountFilteredTxns, filterCategory, search, userAssignments]);

  const categorySummary = useMemo(() => {
    const counts = { [UNASSIGNED]: 0 };
    categories.forEach((c) => { counts[c.name] = 0; });
    accountFilteredTxns.forEach((txn) => {
      const cat = effectiveCategory(txn);
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountFilteredTxns, categories, userAssignments]);

  const sortedTransactions = useMemo(() => {
    const sorted = [...filteredTransactions];
    sorted.sort((a, b) => {
      let aVal, bVal;
      if (sortField === "date") {
        aVal = a.date; bVal = b.date;
      } else if (sortField === "name") {
        aVal = (a.merchant_name || a.name || "").toLowerCase();
        bVal = (b.merchant_name || b.name || "").toLowerCase();
      } else if (sortField === "amount") {
        aVal = Math.abs(a.amount); bVal = Math.abs(b.amount);
      } else {
        aVal = effectiveCategory(a).toLowerCase();
        bVal = effectiveCategory(b).toLowerCase();
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions, sortField, sortDir]);

  const totalPages        = Math.max(1, Math.ceil(sortedTransactions.length / pageSize));
  const safePage          = Math.min(currentPage, totalPages);
  const paginatedTransactions = sortedTransactions.slice((safePage - 1) * pageSize, safePage * pageSize);

  const SortIcon = ({ field }) => (
    <span className={`ml-1 ${sortField === field ? "text-gray-800" : "text-gray-300"}`}>
      {sortField === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  // ── Loading / not linked states ───────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar handleSignOut={handleSignOut}>
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <svg className="animate-spin h-12 w-12 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-gray-400 text-sm">Loading your transactions…</p>
            </div>
          </div>
        </Sidebar>
      </div>
    );
  }

  if (notLinked) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar handleSignOut={handleSignOut}>
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <p className="text-gray-600 text-lg mb-4">No linked bank account found.</p>
              <a href="/BankAccount" className="bg-blue-500 text-white px-5 py-2 rounded-lg hover:bg-blue-600 text-sm">
                Connect Bank Account
              </a>
            </div>
          </div>
        </Sidebar>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
      <div className="flex flex-col flex-1 p-3 sm:p-6">
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 w-full">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Categorize Transactions</h1>
              {classifying && (
                <span className="flex items-center gap-1.5 text-xs text-purple-500 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-full animate-pulse">
                  <span>✦</span> AI classifying…
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setShowBulkAssign((s) => !s); setShowCatManager(false); }}
                className="text-sm border border-purple-300 text-purple-600 px-3 py-1.5 rounded-lg hover:bg-purple-50"
              >
                {showBulkAssign ? "Hide" : "Bulk Assign"}
              </button>
              <button
                onClick={() => { setShowCatManager((s) => !s); setShowBulkAssign(false); }}
                className="text-sm border border-blue-300 text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-50"
              >
                {showCatManager ? "Hide" : "Manage"} Categories
              </button>
            </div>
          </div>
          <p className="text-gray-500 text-sm mb-4">
            Transactions are auto-categorized using AI. Review and correct any that need adjustment —
            your corrections are saved and help improve future AI suggestions.
            <span className="ml-2 text-purple-400 font-medium">✦</span>
            <span className="text-gray-400 text-xs ml-1">= AI Suggested</span>
            <span className="ml-3 text-blue-400 font-medium">✎</span>
            <span className="text-gray-400 text-xs ml-1">= User Defined</span>
          </p>

          {/* Bulk Keyword Assign */}
          {showBulkAssign && (
            <div className="mb-6 border border-purple-100 rounded-lg p-4 bg-purple-50">
              <p className="text-xs font-semibold text-purple-700 uppercase mb-1">Bulk Assign by Keyword</p>
              <p className="text-xs text-purple-500 mb-3">
                Override AI categories for all transactions whose name contains a keyword.
                For example: keyword <em>"check"</em> → category <em>"Labor"</em>.
              </p>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="block text-xs text-gray-500 mb-1 font-medium">Keyword</label>
                  <input
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 w-40"
                    placeholder="e.g. check"
                    value={bulkKeyword}
                    onChange={(e) => setBulkKeyword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleBulkAssign(); }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1 font-medium">Assign to Category</label>
                  <select
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                    value={bulkCategory}
                    onChange={(e) => setBulkCategory(e.target.value)}
                  >
                    <option value="">Select category…</option>
                    {categories.map((c) => (
                      <option key={c.category_id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  {bulkKeyword.trim() && (
                    <p className={`text-xs ${bulkMatchCount > 0 ? "text-purple-600 font-medium" : "text-gray-400"}`}>
                      {bulkMatchCount > 0
                        ? `${bulkMatchCount} transaction${bulkMatchCount !== 1 ? "s" : ""} match`
                        : "No matches"}
                    </p>
                  )}
                  <button
                    onClick={handleBulkAssign}
                    disabled={!bulkKeyword.trim() || !bulkCategory || bulkMatchCount === 0 || bulkApplying}
                    className="bg-purple-500 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-purple-600 disabled:opacity-40 transition"
                  >
                    {bulkApplying ? "Applying…" : `Apply to ${bulkMatchCount || 0}`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Account selector */}
          {accounts.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => { setSelectedAccount("all"); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  selectedAccount === "all"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                }`}
              >
                All Accounts ({transactions.length})
              </button>
              {accounts.map((acc) => (
                <button
                  key={acc.account_id}
                  onClick={() => { setSelectedAccount(acc.account_id); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    selectedAccount === acc.account_id
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                  }`}
                >
                  {acc.name} ({transactions.filter((t) => t.account_id === acc.account_id).length})
                </button>
              ))}
            </div>
          )}

          {/* Category Manager */}
          {showCatManager && (
            <div className="mb-6 border border-blue-100 rounded-lg p-4 bg-blue-50">
              <p className="text-xs font-semibold text-blue-700 uppercase mb-3">Your Categories</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {categories.map((cat) => (
                  <div
                    key={cat.category_id}
                    className="flex items-center gap-1 bg-white border border-gray-200 rounded-full px-3 py-1 text-sm"
                  >
                    <span className="text-gray-700">{cat.name}</span>
                    {cat.is_system ? (
                      <span className="text-xs text-gray-400 ml-1">(default)</span>
                    ) : (
                      <button
                        onClick={() => handleDeleteCategory(cat)}
                        className="text-red-400 hover:text-red-600 ml-1 leading-none text-base"
                        title="Delete"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-48"
                  placeholder="New category name"
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleAddCategory(); }}
                />
                <button
                  onClick={handleAddCategory}
                  disabled={savingCat || !newCatName.trim()}
                  className="bg-blue-500 text-white text-sm px-4 py-1.5 rounded-lg hover:bg-blue-600 disabled:opacity-50"
                >
                  {savingCat ? "Saving..." : "Add"}
                </button>
              </div>
            </div>
          )}

          {/* Category filter — collapsible */}
          <div className="mb-5 border border-gray-200 rounded-lg bg-white shadow-sm">
            <button
              onClick={() => setShowCatFilter((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Filter by Category</span>
                {filterCategory !== "all" && (
                  <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium">
                    {filterCategory}
                  </span>
                )}
              </div>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${showCatFilter ? "rotate-180" : ""}`}
                fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showCatFilter && (
              <div className="px-4 pb-4 flex flex-wrap gap-2">
                <button
                  onClick={() => { setFilterCategory("all"); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                    filterCategory === "all"
                      ? "bg-gray-800 text-white border-gray-800"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  All ({transactions.length})
                </button>
                <button
                  onClick={() => { setFilterCategory(UNASSIGNED); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                    filterCategory === UNASSIGNED
                      ? "bg-yellow-500 text-white border-yellow-500"
                      : "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100"
                  }`}
                >
                  Unassigned ({categorySummary[UNASSIGNED] || 0})
                </button>
                {[...categories].sort((a, b) => a.name.localeCompare(b.name)).map((cat) => (
                  <button
                    key={cat.category_id}
                    onClick={() => { setFilterCategory(cat.name); setCurrentPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                      filterCategory === cat.name
                        ? "bg-green-500 text-white border-green-500"
                        : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                    }`}
                  >
                    {cat.name} ({categorySummary[cat.name] || 0})
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search + page size */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center">
            <input
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 w-full max-w-sm"
              placeholder="Search by merchant or name..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
            <div className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
              <label>Per page</label>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {/* Transactions Table — desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg text-sm">
              <thead>
                <tr className="bg-gray-100 text-gray-600 uppercase text-xs">
                  <th className="px-4 py-3 border text-left cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort("date")}>Date<SortIcon field="date" /></th>
                  <th className="px-4 py-3 border text-left cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort("name")}>Merchant / Name<SortIcon field="name" /></th>
                  <th className="px-4 py-3 border text-right cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort("amount")}>Amount<SortIcon field="amount" /></th>
                  <th className="px-4 py-3 border text-left">Account</th>
                  <th className="px-4 py-3 border text-center w-52 cursor-pointer select-none hover:bg-gray-200" onClick={() => handleSort("category")}>Category<SortIcon field="category" /></th>
                  <th className="px-4 py-3 border text-left w-48">Note</th>
                </tr>
              </thead>
              <tbody>
                {classifying ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <svg className="animate-spin h-8 w-8 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <p className="text-sm font-medium text-purple-600">AI is categorizing your transactions…</p>
                        <p className="text-xs text-gray-400">This usually takes a few seconds. Your table will appear when it's ready.</p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center px-4 py-10 text-gray-400 italic">No transactions found.</td>
                  </tr>
                ) : (
                  paginatedTransactions.map((txn) => {
                    const assignment = userAssignments[txn.transaction_id];
                    const cat     = assignment?.category_name || UNASSIGNED;
                    const source  = assignment?.source ?? null;
                    const isSaving = !!saving[txn.transaction_id];
                    return (
                      <tr key={txn.transaction_id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-2 border text-gray-500 whitespace-nowrap">{txn.date}</td>
                        <td className="px-4 py-2 border font-medium text-gray-800">
                          {txn.merchant_name || txn.name}
                          {txn.merchant_name && txn.name !== txn.merchant_name && (
                            <p className="text-xs text-gray-400 font-normal">{txn.name}</p>
                          )}
                        </td>
                        <td className={`px-4 py-2 border text-right font-semibold ${txn.amount < 0 ? "text-green-600" : "text-red-500"}`}>
                          {txn.amount < 0 ? "+" : ""}${Math.abs(txn.amount).toFixed(2)}
                        </td>
                        <td className="px-4 py-2 border text-gray-500 text-xs whitespace-nowrap">
                          {accountMap[txn.account_id] || "—"}
                        </td>
                        <td className="px-4 py-2 border text-center">
                          <div className="inline-flex items-center gap-1">
                            <select
                              value={cat}
                              disabled={isSaving}
                              onChange={(e) => handleCategoryChange(txn, e.target.value)}
                              className={`text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 ${
                                cat === UNASSIGNED
                                  ? "border-yellow-300 bg-yellow-50 text-yellow-700"
                                  : "border-green-300 bg-green-50 text-green-700"
                              }`}
                            >
                              {categoryOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                            {isSaving
                              ? <span className="text-xs text-gray-400">…</span>
                              : <SourceBadge source={source} />
                            }
                          </div>
                        </td>
                        <td className="px-4 py-2 border">
                          <input
                            type="text"
                            defaultValue={notes[txn.transaction_id] || ""}
                            key={txn.transaction_id + (notes[txn.transaction_id] || "")}
                            onBlur={(e) => handleNoteBlur(txn, e.target.value)}
                            placeholder="Add a note…"
                            className="w-full text-xs text-gray-600 placeholder-gray-300 border-0 focus:outline-none focus:ring-1 focus:ring-green-300 rounded px-1 py-0.5 bg-transparent"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Transactions mobile cards */}
          <div className="md:hidden space-y-2">
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { field: "date",     label: "Date"     },
                { field: "name",     label: "Name"     },
                { field: "amount",   label: "Amount"   },
                { field: "category", label: "Category" },
              ].map(({ field, label }) => (
                <button
                  key={field}
                  onClick={() => handleSort(field)}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    sortField === field
                      ? "bg-gray-700 text-white border-gray-700"
                      : "bg-white text-gray-600 border-gray-300"
                  }`}
                >
                  {label}{sortField === field ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                </button>
              ))}
            </div>
            {classifying ? (
              <div className="flex flex-col items-center gap-3 py-16">
                <svg className="animate-spin h-8 w-8 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <p className="text-sm font-medium text-purple-600">AI is categorizing your transactions…</p>
                <p className="text-xs text-gray-400 text-center">This usually takes a few seconds. Your table will appear when it's ready.</p>
              </div>
            ) : paginatedTransactions.length === 0 ? (
              <p className="text-center py-10 text-gray-400 italic">No transactions found.</p>
            ) : (
              paginatedTransactions.map((txn) => {
                const assignment = userAssignments[txn.transaction_id];
                const cat    = assignment?.category_name || UNASSIGNED;
                const source = assignment?.source ?? null;
                const isSaving = !!saving[txn.transaction_id];
                return (
                  <div key={txn.transaction_id} className="border border-gray-200 rounded-lg p-3 bg-white">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="font-medium text-gray-800 text-sm truncate">{txn.merchant_name || txn.name}</p>
                        <p className="text-xs text-gray-500">
                          {txn.date}
                          {accountMap[txn.account_id] && (
                            <span className="ml-2 text-gray-400">· {accountMap[txn.account_id]}</span>
                          )}
                        </p>
                      </div>
                      <p className={`font-semibold text-sm flex-shrink-0 ${txn.amount < 0 ? "text-green-600" : "text-red-500"}`}>
                        {txn.amount < 0 ? "+" : ""}${Math.abs(txn.amount).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <select
                        value={cat}
                        disabled={isSaving}
                        onChange={(e) => handleCategoryChange(txn, e.target.value)}
                        className={`flex-1 text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 ${
                          cat === UNASSIGNED
                            ? "border-yellow-300 bg-yellow-50 text-yellow-700"
                            : "border-green-300 bg-green-50 text-green-700"
                        }`}
                      >
                        {categoryOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                      {isSaving
                        ? <span className="text-xs text-gray-400">…</span>
                        : <SourceBadge source={source} />
                      }
                    </div>
                    <input
                      type="text"
                      defaultValue={notes[txn.transaction_id] || ""}
                      key={txn.transaction_id + (notes[txn.transaction_id] || "")}
                      onBlur={(e) => handleNoteBlur(txn, e.target.value)}
                      placeholder="Add a note…"
                      className="mt-2 w-full text-xs text-gray-600 placeholder-gray-300 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-300 bg-white"
                    />
                  </div>
                );
              })
            )}
          </div>

          {sortedTransactions.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-2 text-sm text-gray-600">
              <span>
                Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, sortedTransactions.length)} of {sortedTransactions.length}
                {selectedAccount !== "all" && ` · ${accounts.find((a) => a.account_id === selectedAccount)?.name}`}
                {" "}(last 90 days)
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentPage(1)} disabled={safePage === 1}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100" title="First">«</button>
                  <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100" title="Previous">‹</button>
                  {pageNumbers(totalPages, safePage).map((p, i) =>
                    p === "…" ? (
                      <span key={`e${i}`} className="px-2 py-1">…</span>
                    ) : (
                      <button key={p} onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1 rounded border ${p === safePage ? "bg-green-500 text-white border-green-500" : "border-gray-300 hover:bg-gray-100"}`}>
                        {p}
                      </button>
                    )
                  )}
                  <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100" title="Next">›</button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages}
                    className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100" title="Last">»</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </Sidebar>
    </div>
  );
};

export default CategorizeTransactions;
